import React, { useState, useEffect, useCallback } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Box,
    TextField,
    Typography,
    IconButton,
    Button,
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction,
    Divider,
    Chip,
    Tooltip,
    InputAdornment,
    CircularProgress,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
} from '@material-ui/core';
import SearchIcon from '@material-ui/icons/Search';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import AddIcon from '@material-ui/icons/Add';
import DeleteIcon from '@material-ui/icons/Delete';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import SaveIcon from '@material-ui/icons/Save';
import SendIcon from '@material-ui/icons/Send';
import BookmarkIcon from '@material-ui/icons/Bookmark';
import ImageIcon from '@material-ui/icons/Image';
import { makeStyles } from '@material-ui/core/styles';
import api from '../../services/api';
import toastError from '../../errors/toastError';
import { toast } from 'react-toastify';
import ConfirmationModal from '../ConfirmationModal';

const useStyles = makeStyles((theme) => ({
    dialog: {
        '& .MuiDialog-paper': {
            width: 520,
            maxWidth: '95vw',
            maxHeight: '80vh',
        },
    },
    dialogWide: {
        '& .MuiDialog-paper': {
            width: 680,
            maxWidth: '95vw',
            maxHeight: '85vh',
        },
    },
    templateItem: {
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: theme.shape.borderRadius,
        marginBottom: theme.spacing(1),
        cursor: 'pointer',
        '&:hover': {
            backgroundColor: theme.palette.action.hover,
        },
    },
    componentSection: {
        marginBottom: theme.spacing(2),
    },
    componentLabel: {
        fontSize: 11,
        fontWeight: 600,
        letterSpacing: 1,
        color: theme.palette.text.secondary,
        marginBottom: theme.spacing(0.5),
        textTransform: 'uppercase',
    },
    previewBox: {
        backgroundColor: theme.palette.action.hover,
        borderRadius: theme.shape.borderRadius,
        padding: theme.spacing(1.5),
        whiteSpace: 'pre-wrap',
        fontSize: 13,
        marginBottom: theme.spacing(2),
        wordBreak: 'break-word',
    },
    savedChip: {
        cursor: 'pointer',
        marginRight: theme.spacing(0.5),
        marginBottom: theme.spacing(0.5),
    },
    paramRow: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        marginBottom: theme.spacing(1),
    },
    createParamRow: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: theme.spacing(1),
        marginBottom: theme.spacing(1),
    },
    createParamValues: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(0.5),
        flex: 1,
    },
    createParamValueRow: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(0.5),
    },
    sectionLabelBold: {
        fontSize: 11,
        fontWeight: 600,
        letterSpacing: 1,
        color: theme.palette.text.secondary,
        textTransform: 'uppercase',
        marginBottom: theme.spacing(1),
        marginTop: theme.spacing(1),
    },
    savedSection: {
        marginBottom: theme.spacing(2),
    },
    sectionTitle: {
        fontWeight: 600,
        marginBottom: theme.spacing(1),
    },
    actions: {
        padding: theme.spacing(2),
        gap: theme.spacing(1),
    },
}));

// Extrai variáveis por componente (retorna { header: [], body: [], footer: [], buttons: [] })
const extractVariables = (components) => {
    const regex = /\{\{(\d+)\}\}/g;
    const result = { header: [], body: [], footer: [], buttons: [] };

    components.forEach((component) => {
        const type = component.type?.toLowerCase();
        const text = component.text || '';

        if (type === 'header' && ['IMAGE', 'VIDEO'].includes(component.format)) {
            result.header.push({
                type: component.format.toLowerCase(),
                prompt: component.format === 'IMAGE' ? 'URL da imagem' : 'URL do vídeo',
                index: 0,
            });
            return;
        }

        if (type === 'buttons') {
            try {
                const buttons = JSON.parse(component.buttons || '[]');
                buttons.forEach((button, btnIdx) => {
                    if (button.example) {
                        result.buttons.push({ type: button.type, prompt: button.example, buttonIndex: btnIdx, index: result.buttons.length });
                    }
                });
            } catch (_) {}
            return;
        }

        if (result[type] !== undefined) {
            let match;
            regex.lastIndex = 0;
            while ((match = regex.exec(text)) !== null) {
                result[type].push({ type: 'text', prompt: `{{${match[1]}}}`, index: result[type].length });
            }
        }
    });

    return result;
};

// Substitui placeholders no texto com valores fornecidos
const applyValues = (text, vars) => {
    if (!vars || !vars.length) return text;
    let out = text;
    vars.forEach((v, idx) => {
        out = out.replace(`{{${idx + 1}}}`, v.value || `{{${idx + 1}}}`);
    });
    return out;
};

// Monta preview do template com variáveis aplicadas
const buildPreview = (components, varValues) => {
    return components.map((comp) => {
        const type = comp.type?.toLowerCase();
        if (!comp.text) return '';
        const vars = varValues[type] || [];
        return applyValues(comp.text, vars);
    }).filter(Boolean).join('\n');
};

// Monta o variableValues para o backend (formato existente do storeTemplate)
const buildBackendVariables = (componentVarDefs, varValues) => {
    const result = {};
    Object.entries(componentVarDefs).forEach(([type, defs]) => {
        if (!defs.length) return;
        const typeVals = varValues[type] || [];
        const obj = {};
        defs.forEach((def, idx) => {
            obj[idx] = { value: typeVals[idx]?.value || '', buttonIndex: def.buttonIndex ?? 0 };
        });
        result[type] = obj;
    });
    return result;
};

const NEW_TEMPLATE_INITIAL_PARAMS = [];

const TemplateModal = ({ open, handleClose, templates, onSelectTemplate, onTemplateCreated }) => {
    const classes = useStyles();

    // Tela: 'list' | 'params' | 'create'
    const [screen, setScreen] = useState('list');
    const [search, setSearch] = useState('');
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [varDefs, setVarDefs] = useState({ header: [], body: [], footer: [], buttons: [] });
    const [varValues, setVarValues] = useState({});

    // Parametrizações salvas
    const [saved, setSaved] = useState([]);
    const [savedLoading, setSavedLoading] = useState(false);
    const [saveLabel, setSaveLabel] = useState('');
    const [savingLabel, setSavingLabel] = useState(false);
    const [selectedSavedId, setSelectedSavedId] = useState(null);

    // Cadastro de novo template (API Oficial Meta)
    const [newTemplateName, setNewTemplateName] = useState('');
    const [newTemplateParams, setNewTemplateParams] = useState(NEW_TEMPLATE_INITIAL_PARAMS);
    const [creatingTemplate, setCreatingTemplate] = useState(false);

    // Exclusão de template
    const [templateToDelete, setTemplateToDelete] = useState(null);
    const [deletingTemplate, setDeletingTemplate] = useState(false);

    // Upload de imagem para o parâmetro de header (type=IMAGE)
    const [uploadingImageIdx, setUploadingImageIdx] = useState(null);

    const resetModal = () => {
        setScreen('list');
        setSearch('');
        setSelectedTemplate(null);
        setVarDefs({ header: [], body: [], footer: [], buttons: [] });
        setVarValues({});
        setSaved([]);
        setSaveLabel('');
        setSelectedSavedId(null);
        setNewTemplateName('');
        setNewTemplateParams(NEW_TEMPLATE_INITIAL_PARAMS);
    };

    const handleCloseModal = () => {
        resetModal();
        handleClose();
    };

    const fetchSaved = useCallback(async (shortcode) => {
        setSavedLoading(true);
        try {
            const { data } = await api.get('/template-parametrizations', { params: { templateShortcode: shortcode } });
            setSaved(data);
        } catch (err) {
            toastError(err);
        } finally {
            setSavedLoading(false);
        }
    }, []);

    const handleSelectTemplate = (template) => {
        let defs = extractVariables(template.components || []);
        const initValues = {};

        // QuickMessage oficial sem components: lê parâmetros do campo message (JSON)
        if ((template.components || []).length === 0 && template.message) {
            try {
                const parsed = JSON.parse(template.message);
                if (Array.isArray(parsed) && parsed.length > 0) {
                    defs = {
                        header: [],
                        body: parsed.map((p, idx) => ({
                            type: 'text',
                            prompt: p.name || `{{${idx + 1}}}`,
                            index: idx,
                        })),
                        footer: [],
                        buttons: [],
                    };
                    initValues.body = parsed.map((p) => ({
                        name: p.name || '',
                        value: p.values?.[0] || '',
                    }));
                }
            } catch (_) {}
        }

        Object.entries(defs).forEach(([type, defList]) => {
            if (!initValues[type]) {
                initValues[type] = defList.map((d) => ({ name: d.prompt, value: '' }));
            }
        });

        setSelectedTemplate(template);
        setVarDefs(defs);
        setVarValues(initValues);
        setSaveLabel('');
        setSelectedSavedId(null);
        fetchSaved(template.shortcode);
        setScreen('params');
    };

    const handleBack = () => {
        setScreen('list');
        setSelectedTemplate(null);
        setVarDefs({ header: [], body: [], footer: [], buttons: [] });
        setVarValues({});
        setSaved([]);
        setSelectedSavedId(null);
        setNewTemplateName('');
        setNewTemplateParams(NEW_TEMPLATE_INITIAL_PARAMS);
    };

    // ── Cadastro de novo template ──
    const handleOpenCreate = () => {
        setNewTemplateName('');
        setNewTemplateParams(NEW_TEMPLATE_INITIAL_PARAMS);
        setScreen('create');
    };

    const addNewTemplateParam = () => {
        setNewTemplateParams((prev) => [...prev, { name: "", values: [""] }]);
    };

    const removeNewTemplateParam = (idx) => {
        setNewTemplateParams((prev) => prev.filter((_, i) => i !== idx));
    };

    const setNewTemplateParamName = (idx, val) => {
        setNewTemplateParams((prev) => {
            const next = [...prev];
            next[idx] = { ...next[idx], name: val };
            return next;
        });
    };

    const addNewTemplateParamValue = (paramIdx) => {
        setNewTemplateParams((prev) => {
            const next = [...prev];
            next[paramIdx] = { ...next[paramIdx], values: [...next[paramIdx].values, ""] };
            return next;
        });
    };

    const removeNewTemplateParamValue = (paramIdx, valIdx) => {
        setNewTemplateParams((prev) => {
            const next = [...prev];
            const vals = next[paramIdx].values.filter((_, i) => i !== valIdx);
            next[paramIdx] = { ...next[paramIdx], values: vals.length ? vals : [""] };
            return next;
        });
    };

    const setNewTemplateParamValue = (paramIdx, valIdx, val) => {
        setNewTemplateParams((prev) => {
            const next = [...prev];
            const vals = [...next[paramIdx].values];
            vals[valIdx] = val;
            next[paramIdx] = { ...next[paramIdx], values: vals };
            return next;
        });
    };

    const handleSaveNewTemplate = async () => {
        if (!newTemplateName.trim()) {
            toast.warning('Informe o nome do template (igual ao cadastrado na Meta)');
            return;
        }
        setCreatingTemplate(true);
        try {
            await api.post('/quick-messages', {
                shortcode: newTemplateName.trim(),
                message: JSON.stringify(newTemplateParams),
                isOficial: true,
                geral: true,
                visao: true,
                status: 'APPROVED',
                language: 'pt_BR',
            });
            toast.success('Template cadastrado com sucesso');
            if (typeof onTemplateCreated === 'function') await onTemplateCreated();
            setScreen('list');
            setNewTemplateName('');
            setNewTemplateParams(NEW_TEMPLATE_INITIAL_PARAMS);
        } catch (err) {
            toastError(err);
        } finally {
            setCreatingTemplate(false);
        }
    };

    const handleDeleteTemplate = async () => {
        if (!templateToDelete) return;
        setDeletingTemplate(true);
        try {
            await api.delete(`/quick-messages/${templateToDelete.id}`);
            toast.success('Template excluído');
            if (typeof onTemplateCreated === 'function') await onTemplateCreated();
        } catch (err) {
            toastError(err);
        } finally {
            setDeletingTemplate(false);
            setTemplateToDelete(null);
        }
    };

    const setVarField = (type, idx, field, val) => {
        setVarValues((prev) => {
            const arr = [...(prev[type] || [])];
            arr[idx] = { ...arr[idx], [field]: val };
            return { ...prev, [type]: arr };
        });
    };

    // Sobe a imagem escolhida para o S3 e usa a URL pública retornada como
    // valor do parâmetro — evita o usuário ter que hospedar a imagem em
    // outro lugar e colar o link manualmente.
    const handleUploadHeaderImage = async (idx, file) => {
        if (!file) return;

        if (!file.type.startsWith('image/')) {
            toast.warning('Selecione um arquivo de imagem (JPG ou PNG)');
            return;
        }
        if (file.size > 5 * 1024 * 1024) {
            toast.warning('A imagem deve ter no máximo 5 MB');
            return;
        }

        setUploadingImageIdx(idx);
        try {
            const formData = new FormData();
            formData.append('file', file);
            formData.append('typeArch', 'quickMessage');

            const { data } = await api.post('/quick-messages/template-header-image-upload', formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });

            setVarField('header', idx, 'value', data.url);
            toast.success('Imagem enviada');
        } catch (err) {
            toastError(err);
        } finally {
            setUploadingImageIdx(null);
        }
    };

    const addParam = (type) => {
        setVarValues((prev) => {
            const arr = [...(prev[type] || [])];
            arr.push({ name: '', value: '' });
            return { ...prev, [type]: arr };
        });
        setVarDefs((prev) => ({
            ...prev,
            [type]: [...prev[type], { type: 'text', prompt: `{{${prev[type].length + 1}}}`, index: prev[type].length }],
        }));
    };

    const removeParam = (type, idx) => {
        setVarValues((prev) => {
            const arr = [...(prev[type] || [])];
            arr.splice(idx, 1);
            return { ...prev, [type]: arr };
        });
        setVarDefs((prev) => {
            const arr = [...prev[type]];
            arr.splice(idx, 1);
            return { ...prev, [type]: arr };
        });
    };

    const applySaved = (item) => {
        setSelectedSavedId(item.id);
        setSaveLabel(item.label);
        // item.params = [{ name, value }] per-component flat or grouped
        // Salvamos agrupado por tipo: { type: string; name: string; value: string }[]
        const grouped = {};
        (item.params || []).forEach((p) => {
            const type = p.componentType || 'body';
            if (!grouped[type]) grouped[type] = [];
            grouped[type].push({ name: p.name, value: p.value });
        });
        // Merge com varDefs (manter quantidade de campos)
        const newValues = {};
        Object.entries(varDefs).forEach(([type, defs]) => {
            const src = grouped[type] || [];
            newValues[type] = defs.map((_, idx) => src[idx] || { name: '', value: '' });
        });
        setVarValues(newValues);
    };

    const handleSave = async () => {
        if (!saveLabel.trim()) {
            toast.warning('Digite um nome para salvar a parametrização');
            return;
        }
        const params = [];
        Object.entries(varValues).forEach(([type, arr]) => {
            (arr || []).forEach((p) => {
                params.push({ componentType: type, name: p.name || '', value: p.value || '' });
            });
        });
        setSavingLabel(true);
        try {
            if (selectedSavedId) {
                await api.put(`/template-parametrizations/${selectedSavedId}`, {
                    label: saveLabel,
                    params,
                });
                toast.success('Parametrização atualizada');
            } else {
                await api.post('/template-parametrizations', {
                    templateShortcode: selectedTemplate.shortcode,
                    label: saveLabel,
                    params,
                });
                toast.success('Parametrização salva');
            }
            fetchSaved(selectedTemplate.shortcode);
        } catch (err) {
            toastError(err);
        } finally {
            setSavingLabel(false);
        }
    };

    const handleDeleteSaved = async (id) => {
        try {
            await api.delete(`/template-parametrizations/${id}`);
            setSaved((prev) => prev.filter((s) => s.id !== id));
            if (selectedSavedId === id) setSelectedSavedId(null);
            toast.success('Removido');
        } catch (err) {
            toastError(err);
        }
    };

    const handleSend = () => {
        const hasComponents = (selectedTemplate.components || []).length > 0;

        // Todo parâmetro (inclusive a URL de imagem/vídeo do cabeçalho) é
        // obrigatório: enviar vazio faz a Meta rejeitar o template inteiro
        // com um erro genérico, sem indicar qual campo ficou em branco.
        const missingParam = Object.entries(varDefs).some(([type, defs]) =>
            defs.some((_, idx) => !(varValues[type]?.[idx]?.value || '').trim())
        );
        if (missingParam) {
            toast.warning('Preencha todos os parâmetros do template antes de enviar.');
            return;
        }

        const backendVars = buildBackendVariables(varDefs, varValues);

        let preview = '';
        let buttonsArr = [];

        if (hasComponents) {
            preview = buildPreview(selectedTemplate.components, varValues);
            buttonsArr = selectedTemplate.components
                .filter((c) => c.type === 'BUTTONS')
                .map((c) => c.buttons);
        } else {
            // QuickMessage sem components: monta preview com os valores preenchidos
            const bodyVals = varValues.body || [];
            preview = bodyVals.map((v, i) => `{{${i + 1}}}: ${v.value}`).join('\n');
        }

        onSelectTemplate({
            ...selectedTemplate,
            variables: backendVars,
            bodyToSave: preview || selectedTemplate.shortcode,
            _buttonsArr: buttonsArr,
        });
        handleCloseModal();
    };

    const filteredTemplates = templates.filter((t) =>
        t?.shortcode?.toLowerCase().includes(search.toLowerCase())
    );

    const hasAnyVar = Object.values(varDefs).some((arr) => arr.length > 0);
    const preview = selectedTemplate ? buildPreview(selectedTemplate.components || [], varValues) : '';

    const componentLabels = { header: 'Cabeçalho', body: 'Corpo', footer: 'Rodapé', buttons: 'Botões' };

    return (
        <Dialog open={open} onClose={handleCloseModal} className={screen === 'create' ? classes.dialogWide : classes.dialog} fullWidth>
            <DialogTitle>
                <Box display="flex" alignItems="center" justifyContent="space-between" gap={8}>
                    <Box display="flex" alignItems="center" gap={8}>
                        {screen !== 'list' && (
                            <IconButton size="small" onClick={handleBack} style={{ marginRight: 8 }}>
                                <ArrowBackIcon />
                            </IconButton>
                        )}
                        <span>
                            {screen === 'list' && 'Templates do WhatsApp'}
                            {screen === 'params' && `Template: ${selectedTemplate?.shortcode}`}
                            {screen === 'create' && 'Novo Template'}
                        </span>
                    </Box>
                    {screen === 'list' && (
                        <Button
                            size="small"
                            variant="outlined"
                            color="primary"
                            startIcon={<AddIcon />}
                            onClick={handleOpenCreate}
                        >
                            Novo Template
                        </Button>
                    )}
                </Box>
            </DialogTitle>

            <DialogContent dividers style={{ padding: 16 }}>
                {/* ── LISTA DE TEMPLATES ── */}
                {screen === 'list' && (
                    <>
                        <TextField
                            variant="outlined"
                            size="small"
                            placeholder="Pesquisar templates..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            fullWidth
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon fontSize="small" />
                                    </InputAdornment>
                                ),
                            }}
                            style={{ marginBottom: 12 }}
                        />
                        {filteredTemplates.length === 0 && (
                            <Typography variant="body2" color="textSecondary" align="center" style={{ marginTop: 24 }}>
                                Nenhum template encontrado
                            </Typography>
                        )}
                        <List disablePadding>
                            {filteredTemplates.map((template) => (
                                <ListItem
                                    key={template.id}
                                    className={classes.templateItem}
                                    onClick={() => handleSelectTemplate(template)}
                                >
                                    <ListItemText
                                        primary={
                                            <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                                                <Typography variant="body2" style={{ fontWeight: 600 }}>
                                                    {template.shortcode}
                                                </Typography>
                                                {template.language && (
                                                    <Chip size="small" label={template.language} />
                                                )}
                                                {template.category && (
                                                    <Chip size="small" label={template.category} variant="outlined" />
                                                )}
                                            </Box>
                                        }
                                        secondary={
                                            <Typography variant="caption" color="textSecondary" style={{ whiteSpace: 'pre-wrap' }}>
                                                {(template.components || []).map((c) => c.text).filter(Boolean).join('\n')}
                                            </Typography>
                                        }
                                    />
                                    <ListItemSecondaryAction>
                                        <Tooltip title="Excluir template">
                                            <IconButton
                                                size="small"
                                                edge="end"
                                                disabled={deletingTemplate}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setTemplateToDelete(template);
                                                }}
                                            >
                                                <DeleteOutlineIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </ListItemSecondaryAction>
                                </ListItem>
                            ))}
                        </List>
                    </>
                )}

                {/* ── PARÂMETROS ── */}
                {screen === 'params' && (
                    <>
                        {/* Parametrizações salvas */}
                        {savedLoading && <CircularProgress size={16} style={{ marginBottom: 8 }} />}
                        {!savedLoading && saved.length > 0 && (
                            <Box className={classes.savedSection}>
                                <Typography variant="caption" className={classes.sectionTitle}>
                                    Parametrizações salvas
                                </Typography>
                                <Box display="flex" flexWrap="wrap">
                                    {saved.map((s) => (
                                        <Chip
                                            key={s.id}
                                            icon={<BookmarkIcon style={{ fontSize: 14 }} />}
                                            label={s.label}
                                            size="small"
                                            color={selectedSavedId === s.id ? 'primary' : 'default'}
                                            className={classes.savedChip}
                                            onClick={() => applySaved(s)}
                                            onDelete={() => handleDeleteSaved(s.id)}
                                        />
                                    ))}
                                </Box>
                                <Divider style={{ marginTop: 8, marginBottom: 12 }} />
                            </Box>
                        )}

                        {/* Campos por componente */}
                        {Object.entries(varDefs).map(([type, defs]) => {
                            const vals = varValues[type] || [];
                            if (defs.length === 0 && type !== 'body') return null;
                            if (defs.length === 0) return null;
                            return (
                                <Box key={type} className={classes.componentSection}>
                                    <Box display="flex" alignItems="center" justifyContent="space-between">
                                        <Typography className={classes.componentLabel}>
                                            {componentLabels[type] || type}
                                        </Typography>
                                        <Tooltip title="Adicionar parâmetro">
                                            <IconButton size="small" onClick={() => addParam(type)}>
                                                <AddIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                    {defs.map((def, idx) => (
                                        <Box key={idx} className={classes.paramRow}>
                                            <TextField
                                                size="small"
                                                label="Nome do parâmetro"
                                                value={vals[idx]?.name || ''}
                                                onChange={(e) => setVarField(type, idx, 'name', e.target.value)}
                                                style={{ flex: 1 }}
                                                variant="outlined"
                                            />
                                            <TextField
                                                size="small"
                                                label="Valor"
                                                value={vals[idx]?.value || ''}
                                                onChange={(e) => setVarField(type, idx, 'value', e.target.value)}
                                                style={{ flex: 1.5 }}
                                                variant="outlined"
                                                InputProps={type === 'header' && def.type === 'image' ? {
                                                    endAdornment: (
                                                        <InputAdornment position="end">
                                                            {uploadingImageIdx === idx ? (
                                                                <CircularProgress size={18} />
                                                            ) : (
                                                                <Tooltip title="Enviar imagem">
                                                                    <IconButton
                                                                        size="small"
                                                                        component="label"
                                                                        edge="end"
                                                                    >
                                                                        <ImageIcon fontSize="small" />
                                                                        <input
                                                                            type="file"
                                                                            accept="image/jpeg,image/png"
                                                                            hidden
                                                                            onChange={(e) => {
                                                                                const file = e.target.files?.[0];
                                                                                handleUploadHeaderImage(idx, file);
                                                                                e.target.value = '';
                                                                            }}
                                                                        />
                                                                    </IconButton>
                                                                </Tooltip>
                                                            )}
                                                        </InputAdornment>
                                                    ),
                                                } : undefined}
                                            />
                                            <IconButton size="small" onClick={() => removeParam(type, idx)}>
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </Box>
                                    ))}
                                </Box>
                            );
                        })}

                        {/* Preview */}
                        {preview && (
                            <>
                                <Divider style={{ marginBottom: 12 }} />
                                <Typography variant="caption" className={classes.componentLabel}>
                                    Pré-visualização
                                </Typography>
                                <Box className={classes.previewBox}>{preview}</Box>
                            </>
                        )}

                        {/* Nome para salvar */}
                        <TextField
                            size="small"
                            label="Nome da parametrização (para salvar)"
                            value={saveLabel}
                            onChange={(e) => setSaveLabel(e.target.value)}
                            fullWidth
                            variant="outlined"
                            style={{ marginTop: 8 }}
                        />
                    </>
                )}

                {/* ── CADASTRO DE NOVO TEMPLATE ── */}
                {screen === 'create' && (
                    <>
                        <TextField
                            autoFocus
                            label="Nome do template (igual ao cadastrado na Meta)"
                            value={newTemplateName}
                            onChange={(e) => setNewTemplateName(e.target.value)}
                            helperText="Deve ser o nome exato do template aprovado na Meta"
                            variant="outlined"
                            margin="dense"
                            fullWidth
                        />

                        <Divider style={{ marginTop: 8, marginBottom: 4 }} />
                        <Box display="flex" alignItems="center" justifyContent="space-between">
                            <Typography className={classes.sectionLabelBold}>
                                Parâmetros do template
                            </Typography>
                            <Button
                                size="small"
                                startIcon={<AddIcon />}
                                onClick={addNewTemplateParam}
                                color="primary"
                            >
                                Adicionar parâmetro
                            </Button>
                        </Box>

                        <Box display="flex" style={{ gap: 8, marginBottom: 4 }}>
                            <Typography variant="caption" color="textSecondary" style={{ flex: 1, fontWeight: 600 }}>
                                Nome do parâmetro
                            </Typography>
                            <Typography variant="caption" color="textSecondary" style={{ flex: 2, fontWeight: 600 }}>
                                Valores possíveis
                            </Typography>
                            <Box style={{ width: 32 }} />
                        </Box>

                        {newTemplateParams.map((param, pIdx) => (
                            <Box key={pIdx} className={classes.createParamRow}>
                                <TextField
                                    size="small"
                                    variant="outlined"
                                    placeholder={`Parâmetro ${pIdx + 1}`}
                                    value={param.name}
                                    onChange={(e) => setNewTemplateParamName(pIdx, e.target.value)}
                                    style={{ flex: 1 }}
                                />
                                <Box className={classes.createParamValues}>
                                    {param.values.map((val, vIdx) => (
                                        <Box key={vIdx} className={classes.createParamValueRow}>
                                            <TextField
                                                size="small"
                                                variant="outlined"
                                                placeholder={`Valor ${vIdx + 1}`}
                                                value={val}
                                                onChange={(e) => setNewTemplateParamValue(pIdx, vIdx, e.target.value)}
                                                style={{ flex: 1 }}
                                            />
                                            {param.values.length > 1 && (
                                                <IconButton size="small" onClick={() => removeNewTemplateParamValue(pIdx, vIdx)}>
                                                    <DeleteOutlineIcon fontSize="small" />
                                                </IconButton>
                                            )}
                                        </Box>
                                    ))}
                                    <Button
                                        size="small"
                                        startIcon={<AddIcon />}
                                        onClick={() => addNewTemplateParamValue(pIdx)}
                                        style={{ alignSelf: 'flex-start', marginTop: 2 }}
                                    >
                                        Valor
                                    </Button>
                                </Box>
                                <IconButton size="small" onClick={() => removeNewTemplateParam(pIdx)} style={{ marginTop: 4 }}>
                                    <DeleteOutlineIcon />
                                </IconButton>
                            </Box>
                        ))}

                        {newTemplateParams.length === 0 && (
                            <Typography variant="body2" color="textSecondary" align="center" style={{ marginTop: 16 }}>
                                Nenhum parâmetro cadastrado. Clique em "Adicionar parâmetro" para começar.
                            </Typography>
                        )}
                    </>
                )}
            </DialogContent>

            {screen === 'params' && (
                <DialogActions className={classes.actions}>
                    <Button
                        variant="outlined"
                        startIcon={<SaveIcon />}
                        onClick={handleSave}
                        disabled={savingLabel}
                        size="small"
                    >
                        {savingLabel ? 'Salvando...' : selectedSavedId ? 'Atualizar' : 'Salvar'}
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<SendIcon />}
                        onClick={handleSend}
                        size="small"
                    >
                        Enviar
                    </Button>
                </DialogActions>
            )}

            {screen === 'create' && (
                <DialogActions className={classes.actions}>
                    <Button
                        variant="outlined"
                        color="secondary"
                        onClick={handleBack}
                        disabled={creatingTemplate}
                        size="small"
                    >
                        Cancelar
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleSaveNewTemplate}
                        disabled={creatingTemplate}
                        size="small"
                    >
                        {creatingTemplate ? <CircularProgress size={18} /> : 'Adicionar'}
                    </Button>
                </DialogActions>
            )}

            <ConfirmationModal
                title="Excluir template"
                open={!!templateToDelete}
                onClose={() => setTemplateToDelete(null)}
                onConfirm={handleDeleteTemplate}
            >
                {`Tem certeza que deseja excluir o template "${templateToDelete?.shortcode}"? Essa ação não pode ser desfeita.`}
            </ConfirmationModal>
        </Dialog>
    );
};

export default TemplateModal;
